import {
  Controller,
  useFormContext,
  UseFormReturn,
  ControllerRenderProps,
  useFieldArray,
  get,
} from "react-hook-form";
import React, { useEffect, useCallback, useState, useMemo } from "react";
import { ErrorCode } from "react-dropzone";
import GenericUploadInput from "@ui-kit/form/GenericUploadInput/GenericUploadInput.web";
import DocumentUploadInput from "@ui-kit/form/DocumentUploadInput/DocumentUploadInput.web";
import FormControl, {
  FormHelperText,
} from "@ui-kit/form/FormControl/FormControl.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Stack from "@ui-kit/layout/Stack/Stack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import RadioGroup from "@ui-kit/form/RadioGroup/RadioGroup.web";
import Radio from "@ui-kit/form/Radio/Radio.web";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";
import FormRow from "@ui-kit/form/FormRow/FormRow.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import Input, {
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@ui-kit/form/Input/Input.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Checkbox from "@ui-kit/form/Checkbox/Checkbox.web";
import ImageUploadInput from "@ui-kit/form/ImageUploadInput/ImageUploadInput.web";
import Button from "@ui-kit/form/Button/Button.web";
import Card from "@ui-kit/data-display/Card/Card.web";
import Icon from "@ui-kit/icons/Icon.web";
import Select from "@ui-kit/form/Select/Select.web";
import Textarea from "@ui-kit/form/Textarea/Textarea.web";
import { FontFamilyField } from "@ui-kit/form/FontFamilyField/FontFamilyField.web";
import InputDatePicker from "@shared/components/ui-kit/form/InputDatePicker/InputDatePicker.web";
import {
  FormFieldConfig,
  GroupFormFieldConfig,
  SingleFieldConfig,
  FieldArrayFormFieldConfig,
  SelectFormFieldConfig,
  TextareaFormFieldConfig,
  RichtextFormFieldConfig,
  DateFormFieldConfig,
  DatetimeFormFieldConfig,
  FORM_FIELD_TYPES,
  ImageFormFieldConfig,
  RepeaterFormFieldConfig,
  NumberFormFieldConfig,
  NumberInputAddon,
  CheckboxFormFieldConfig,
  RadioFormFieldConfig,
  SearchFormFieldConfig,
  SearchEventFormFieldConfig,
  SearchProductFormFieldConfig,
  SearchMembershipFormFieldConfig,
} from "@shared/components/application/form/FormField/types";
import SearchItem from "@ui-kit/data-display/SearchItem/SearchItem.web";
import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";

import useDebounce from "@shared/hooks/useDebounce";
import NumericFormat from "@ui-kit/form/NumericFormat/NumericFormat.web";
import PhoneNumberInput from "@shared/components/ui-kit/form/PhoneNumberInput/PhoneNumberInput.web";
import { PhoneCountryType } from "@shared/lib/libphonenumber/api";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Switch from "@ui-kit/form/Switch/Switch.web";
import RichTextEditor from "@ui-kit/form/RichTextEditor/RichTextEditor.web";
// eslint-disable-next-line import/no-cycle -- Intentional: RepeaterField renders FormField for nested items
import RepeaterField from "../RepeaterField/RepeaterField.web";
import {
  SearchEventField,
  SearchProductField,
  SearchMembershipField,
} from "./SearchFormFields.web";

export interface GenerateFormFieldProps {
  config: FormFieldConfig;
}

function getInputSafeProps(
  config: SingleFieldConfig
): Omit<
  SingleFieldConfig,
  | "validationRules"
  | "defaultValue"
  | "required"
  | "label"
  | "hideLabel"
  | "helpText"
  | "width"
  | "pt"
  | "hidden"
  | "type"
  | "tooltip"
  | "disabled"
> {
  const {
    validationRules: __validationRules,
    defaultValue: __defaultValue,
    required: __required,
    label: __label,
    hideLabel: __hideLabel,
    helpText: __helpText,
    width: __width,
    pt: __pt,
    hidden: __hidden,
    type: __type,
    tooltip: __tooltip,
    disabled: __disabled,
    ...inputProps
  } = config;

  return inputProps;
}

/**
 * GROUP Field Component
 *
 * Renders a group of fields with flexible layout options.
 */
function GroupField({
  config,
}: {
  config: GroupFormFieldConfig;
}): JSX.Element | null {
  if (config.hidden) {
    return null;
  }

  const { layout = {}, fields } = config;
  const {
    direction = "row",
    gap = "16px",
    columns,
    flexWrap = "wrap",
  } = layout;

  return (
    <Box width="100%">
      {config.label && (
        <Box mb="xs">
          <FormLabel fontSize="sm">{config.label}</FormLabel>
        </Box>
      )}

      <Box
        display={columns ? "grid" : "flex"}
        flexDirection={columns ? undefined : direction}
        gridTemplateColumns={columns ? `repeat(${columns}, 1fr)` : undefined}
        gap={gap}
        flexWrap={columns ? undefined : flexWrap}
        width="100%"
      >
        {fields.map((fieldConfig, index) => {
          const fieldKey = fieldConfig.name || `group-field-${index}`;
          const fieldWidth = fieldConfig.width;

          return (
            <Box
              key={fieldKey}
              width={fieldWidth || (columns ? undefined : "100%")}
              flex={
                !fieldWidth && !columns && direction === "row" ? 1 : undefined
              }
            >
              <SingleFormField config={fieldConfig} />
            </Box>
          );
        })}
      </Box>

      {config.helpText && (
        <Text fontSize="sm" mt="2xs" color="gray.600">
          {config.helpText}
        </Text>
      )}
    </Box>
  );
}

/**
 * FIELD_ARRAY Component
 *
 * Renders an array of field groups with add/remove functionality.
 * Each item in the array contains the same set of sub-fields.
 */
function FieldArrayField({
  config,
}: {
  config: FieldArrayFormFieldConfig;
}): JSX.Element | null {
  const form = useFormContext();

  if (!form) {
    throw new Error(
      `FieldArrayField: No form instance found for field "${config.name}". ` +
        `Wrap your form with <FormProvider> or pass the form instance.`
    );
  }

  if (config.hidden) {
    return null;
  }

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: config.name,
  });

  const {
    minItems = 1,
    maxItems,
    addButtonText = "Add Item",
    removeButtonLabel = "Remove",
    showItemLabel = true,
    itemLabelTemplate = "Item {index}",
    itemLayout = {},
    itemContainerProps = {},
  } = config;

  const { direction = "column", gap = "12px" } = itemLayout;
  const {
    bg = "gray.50",
    p = "16px",
    borderRadius = "md",
  } = itemContainerProps;

  const getDefaultItemValues = useCallback(() => {
    const defaultItem: Record<string, unknown> = {};
    config.fields.forEach((field) => {
      defaultItem[field.name] = field.defaultValue ?? "";
    });
    return defaultItem;
  }, [config.fields]);

  const handleAddItem = useCallback(() => {
    if (maxItems && fields.length >= maxItems) {
      return;
    }
    append(getDefaultItemValues());
  }, [maxItems, fields.length, append, getDefaultItemValues]);

  const handleRemoveItem = useCallback(
    (index: number) => {
      if (fields.length <= minItems) {
        return;
      }
      remove(index);
    },
    [fields.length, minItems, remove]
  );

  // Get errors for the array field
  const arrayErrors = form.formState.errors[config.name];

  return (
    <Box
      width="100%"
      data-testid={config.testId || `${config.name}-field-array`}
    >
      {config.label && (
        <Box mb="xs">
          <FormLabel fontSize="sm">{config.label}</FormLabel>
        </Box>
      )}

      <Stack spacing="16px">
        {fields.map((item, index) => (
          <Card key={item.id} bg={bg} p={p} borderRadius={borderRadius}>
            <Flex justify="space-between" align="center" mb="sm">
              {showItemLabel && (
                <Text fontWeight="bold" fontSize="sm">
                  {itemLabelTemplate.replace("{index}", String(index + 1))}
                </Text>
              )}
              {fields.length > minItems && (
                <IconButton
                  aria-label={removeButtonLabel}
                  iconName="Trash"
                  iconColor="red.500"
                  iconSize="16px"
                  size="sm"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleRemoveItem(index)}
                  data-testid={`${config.name}-remove-item-${index}`}
                />
              )}
            </Flex>

            <Box
              display="flex"
              flexDirection={direction}
              gap={gap}
              width="100%"
            >
              {config.fields.map((fieldConfig) => {
                // Create a modified config with the nested field name path
                const nestedFieldConfig = {
                  ...fieldConfig,
                  name: `${config.name}.${index}.${fieldConfig.name}`,
                };

                return (
                  <SingleFormField
                    key={nestedFieldConfig.name}
                    config={nestedFieldConfig}
                  />
                );
              })}
            </Box>
          </Card>
        ))}
      </Stack>

      {/* Array-level error (e.g., min/max items) */}
      {arrayErrors &&
        typeof arrayErrors === "object" &&
        "message" in arrayErrors && (
          <FormErrorMessage mt="xs">
            {String((arrayErrors as { message?: string }).message || "")}
          </FormErrorMessage>
        )}

      <Flex justify="flex-start" mt="md">
        <Button
          variant="outline"
          leftIcon={<Icon.Plus />}
          onClick={handleAddItem}
          isDisabled={maxItems ? fields.length >= maxItems : false}
          data-testid={`${config.name}-add-item`}
        >
          {addButtonText}
        </Button>
      </Flex>

      {config.helpText && (
        <FormHelperText fontSize="sm" mt="xs">
          {config.helpText}
        </FormHelperText>
      )}
    </Box>
  );
}

/**
 * Single Form Field Component (non-group fields)
 *
 * Renders individual form fields with validation and error handling.
 */
function SingleFormField({
  config,
}: {
  config: SingleFieldConfig;
}): JSX.Element | null {
  const form = useFormContext();

  if (!form) {
    throw new Error(
      `FormField: No form instance found for field "${config.name}". ` +
        `Wrap your form with <FormProvider> or pass the form instance.`
    );
  }

  if (config.hidden) {
    return null;
  }

  // Use react-hook-form's get helper for nested paths (e.g., "denominations.0.value")
  const error = get(form.formState.errors, config.name);
  const hasError = Boolean(error);
  const isRequired =
    config.required ||
    config.validationRules?.some((r) => r.type === "required");
  const showOptionalTag = config.showOptionalTag ?? true;

  const renderLabel = () => {
    if (!config.label || config.hideLabel) return null;

    const labelContent = (
      <FormLabel
        fontSize="sm"
        htmlFor={config.name}
        isOptional={showOptionalTag && !isRequired}
      >
        {config.label}
      </FormLabel>
    );

    // If there's a tooltip, wrap label with tooltip and info icon
    if (config.tooltip) {
      return (
        <Flex alignItems="center" gap="2xs" mb="2xs">
          {labelContent}
          <Tooltip
            label={config.tooltip}
            placement="top"
            hasArrow
            shouldWrapChildren
          >
            <Box as="span" cursor="help" display="inline-flex">
              <Icon.Info
                width="element.sm"
                height="element.sm"
                color="gray.400"
              />
            </Box>
          </Tooltip>
        </Flex>
      );
    }

    return labelContent;
  };

  // REPEATER fields use useFieldArray internally, so they don't need Controller wrapper
  if (config.type === FORM_FIELD_TYPES.REPEATER) {
    return (
      <>
        <FormRow>
          <FormControl
            isInvalid={hasError}
            isDisabled={config.disabled}
            width={config.width}
            pt={config.pt}
            isRequired={Boolean(isRequired)}
          >
            {renderLabel()}

            <RepeaterField config={config as RepeaterFormFieldConfig} />

            {config.helpText && !hasError && (
              <FormHelperText fontSize="sm">{config.helpText}</FormHelperText>
            )}

            {hasError && (
              <FormErrorMessage>
                {String(error?.message || "")}
              </FormErrorMessage>
            )}
          </FormControl>
        </FormRow>
      </>
    );
  }

  // Wrap input with tooltip if label is hidden and tooltip exists
  const renderInputWithTooltip = (input: JSX.Element) => {
    if (config.hideLabel && config.tooltip) {
      return (
        <Tooltip label={config.tooltip} placement="top" hasArrow>
          <Box>{input}</Box>
        </Tooltip>
      );
    }
    return input;
  };

  return (
    <>
      <FormRow>
        <FormControl
          isInvalid={hasError}
          isDisabled={config.disabled}
          width={config.width}
          pt={config.pt}
          alignItems={config.alignItems}
          isRequired={Boolean(isRequired)}
        >
          {renderLabel()}

          <Controller
            name={config.name}
            control={form.control}
            render={({ field }) =>
              renderInputWithTooltip(
                renderInput(config, field, form) as JSX.Element
              )
            }
          />

          {config.helpText && !hasError && (
            <Text fontSize="sm" color="gray.600">
              {config.helpText}
            </Text>
          )}

          {hasError && (
            <FormErrorMessage>{String(error?.message || "")}</FormErrorMessage>
          )}
        </FormControl>
      </FormRow>
    </>
  );
}

/**
 * Generate Form Field Component
 *
 * Automatically generates and renders a form field based on configuration.
 * Supports single fields, GROUP fields, and FIELD_ARRAY fields.
 *
 * Usage:
 * ```tsx
 * <FormProvider {...form}>
 *   <FormField config={config} />
 * </FormProvider>
 * ```
 *
 * Handles validation, error messages, and consistent styling.
 * Works seamlessly with Yup schema validation.
 */
export default function FormField({
  config,
}: GenerateFormFieldProps): JSX.Element | null {
  if (config.type === FORM_FIELD_TYPES.GROUP) {
    return <GroupField config={config} />;
  }

  // Handle SECTION and CUSTOM types - these are handled by Form.web.tsx
  if (
    config.type === FORM_FIELD_TYPES.SECTION ||
    config.type === FORM_FIELD_TYPES.CUSTOM
  ) {
    return null;
  }

  if (config.type === FORM_FIELD_TYPES.FIELD_ARRAY) {
    return <FieldArrayField config={config} />;
  }

  // Handle single field types
  return <SingleFormField config={config} />;
}

function PasswordInput({
  field,
  config,
}: {
  field: ControllerRenderProps;
  config: Extract<FormFieldConfig, { type: typeof FORM_FIELD_TYPES.PASSWORD }>;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const inputSafeProps = getInputSafeProps(config);
  return (
    <InputGroup>
      <Input
        {...field}
        id={config.name}
        data-testid={config.testId || config.name}
        type={showPassword ? "text" : "password"}
        autoComplete={config.autoComplete || "current-password"}
        {...inputSafeProps}
      />
      <InputRightElement>
        <IconButton
          aria-label={showPassword ? "Hide password" : "Show password"}
          onClick={() => setShowPassword((prev) => !prev)}
          fontSize="xl"
          iconName={showPassword ? "Hide" : "Show"}
          iconColor="font.dark"
          data-testid={`${config.name}-toggle-password-visibility`}
        />
      </InputRightElement>
    </InputGroup>
  );
}

function FileUploadInput({
  field,
  config,
  form,
}: {
  field: ControllerRenderProps;
  form: UseFormReturn;
  config: Extract<FormFieldConfig, { type: typeof FORM_FIELD_TYPES.FILE }>;
}) {
  const [currentFile, setCurrentFile] = useState<File | undefined>(() => {
    const { value } = field;
    if (value instanceof File) {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }
    return undefined;
  });

  // Sync field.value with currentFile state
  useEffect(() => {
    const { value } = field;
    if (value instanceof File) {
      setCurrentFile(value);
    } else if (Array.isArray(value) && value.length > 0) {
      setCurrentFile(value[0]);
    } else {
      setCurrentFile(undefined);
    }
  }, [field.value]);

  // Helper functions to detect file type
  const isCsvType = useCallback((accept?: string) => accept === ".csv", []);

  const isDocumentType = useCallback(
    (accept?: string) =>
      accept === ".pdf" || accept === ".doc" || accept === ".docx",
    []
  );

  const isImageType = useCallback(
    (accept?: string) =>
      accept === ".png" || accept === ".jpg" || accept === ".jpeg",
    []
  );

  // Error codes
  const defaultErrorMessage =
    config.maxSizeErrorMessage || "File cannot be larger than the allowed size";
  const maxSizeMB = config.maxSizeBytes
    ? (config.maxSizeBytes / (1024 * 1024)).toFixed(0)
    : "20";

  // Image upload handlers - stores data URL string in RHF
  const updateImage = useCallback(
    (value: string | undefined) => {
      field.onChange(value ?? null);
      if (!value) {
        form.clearErrors(config.name);
      }
    },
    [field, form, config.name]
  );

  // Document upload handlers - stores data URL string in RHF
  const setDocument = useCallback(
    (value: string) => {
      field.onChange(value || null);
      if (!value) {
        form.clearErrors(config.name);
      }
    },
    [field, form, config.name]
  );

  // Generic upload handlers - stores File object in RHF
  const document = useMemo(() => {
    if (currentFile) {
      return currentFile.name;
    }
    return undefined;
  }, [currentFile]);

  const updateCurrentFile = useCallback((file: File | undefined) => {
    setCurrentFile(file);
  }, []);

  const updateDocument = useCallback(
    (doc: string | undefined) => {
      if (!doc) {
        field.onChange(config.multiple ? [] : null);
        setCurrentFile(undefined);
        form.clearErrors(config.name);
      }
    },
    [field, form, config.name, config.multiple]
  );

  const onDrop = useCallback(
    (fileList: Array<File>) => {
      if (fileList.length === 0) {
        return;
      }

      const file = fileList[0];
      setCurrentFile(file);

      const value = config.multiple ? fileList : file;
      field.onChange(value);
      config.onFileSelect?.(value, null);

      form.clearErrors(config.name);
    },
    [field, form, config.name, config.multiple, config.onFileSelect]
  );

  // Fallback to basic input if no maxSizeBytes
  if (!config.maxSizeBytes) {
    const inputProps = {
      id: config.name,
      "data-testid": config.testId || config.name,
      type: "file" as const,
      accept: config.accept,
      multiple: config.multiple,
      name: field.name,
      onBlur: field.onBlur,
      onChange: field.onChange,
      ref: field.ref,
    };
    return <Input {...inputProps} />;
  }

  // Use ImageUploadInput for image types
  if (isImageType(config.accept)) {
    const imageHandleErrorCodes = {
      [ErrorCode.FileTooLarge]: defaultErrorMessage,
      [ErrorCode.FileInvalidType]: `Only ${
        config.accept?.toUpperCase().replace(".", "") || "PNG/JPG"
      } files are supported`,
      [ErrorCode.TooManyFiles]: "You can only upload one file",
    };

    return (
      <ImageUploadInput
        id={config.name}
        maxFileSize={config.maxSizeBytes}
        helpText={config.label || "Drop an image here or click to upload"}
        subHelpText={
          config.helpText ||
          `Image must be less than ${maxSizeMB}MB and in ${
            config.accept?.toUpperCase().replace(".", "") || "PNG/JPG"
          } format.`
        }
        image={typeof field.value === "string" ? field.value : undefined}
        updateImage={updateImage}
        minWidth={config.minWidth ?? 100}
        minHeight={config.minHeight ?? 100}
        maxWidth={config.maxWidth ?? 2000}
        maxHeight={config.maxHeight ?? 2000}
        handleErrorCodes={imageHandleErrorCodes}
      />
    );
  }

  // Use DocumentUploadInput for document types
  if (isDocumentType(config.accept)) {
    const documentHandleErrorCodes = {
      [ErrorCode.FileTooLarge]: defaultErrorMessage,
      [ErrorCode.FileInvalidType]: `Only ${
        config.accept?.toUpperCase().replace(".", "") || "PDF"
      } files are supported`,
      [ErrorCode.TooManyFiles]: "You can only upload one file",
    };

    return (
      <DocumentUploadInput
        id={config.name}
        maxFileSize={config.maxSizeBytes}
        helpText={config.label || "Drop a document here or click to upload"}
        subHelpText={
          config.helpText ||
          `Document must be less than ${maxSizeMB}MB and in ${
            config.accept?.toUpperCase().replace(".", "") || "PDF"
          } format.`
        }
        document={typeof field.value === "string" ? field.value : ""}
        setDocument={setDocument}
        handleErrorCodes={documentHandleErrorCodes}
      />
    );
  }

  // Use sheet upload style for CSV files (mirrors SheetUploadInput behavior)
  if (isCsvType(config.accept)) {
    const sheetHandleErrorCodes = {
      [ErrorCode.FileTooLarge]: defaultErrorMessage,
      [ErrorCode.FileInvalidType]: "Only CSV files are supported",
      [ErrorCode.TooManyFiles]: "You can only upload one file",
    };

    // SheetUploadInput passes the File object directly via setDocument
    const setSheetDocument = (value: string | File | undefined) => {
      if (value instanceof File) {
        field.onChange(value);
        setCurrentFile(value);
        config.onFileSelect?.(value, null);
      } else if (!value) {
        field.onChange(config.multiple ? [] : null);
        setCurrentFile(undefined);
      }
      form.clearErrors(config.name);
    };

    return (
      <GenericUploadInput
        id={config.name}
        maxFileSize={config.maxSizeBytes}
        helpText={config.label || "Drop a file here or click to upload"}
        subHelpText={
          config.helpText ||
          `File must be less than ${maxSizeMB}MB and in CSV format.`
        }
        document={document}
        updateDocument={setSheetDocument}
        onDrop={onDrop}
        currentFile={currentFile}
        updateCurrentFile={updateCurrentFile}
        acceptedFileTypes={{
          "text/csv": [".csv"],
        }}
        dropzoneIcon={
          <Icon.Document
            boxSize="element.4xl"
            color="gray.300"
            marginBottom="sm"
          />
        }
        removeButtonIcon={
          <Icon.Document color="gray.300" boxSize="element.4xl" />
        }
        handleErrorCodes={sheetHandleErrorCodes}
      />
    );
  }

  // Use GenericUploadInput as fallback for all other file types
  const fileExtension = config.accept?.toUpperCase().replace(".", "") || "file";

  return (
    <GenericUploadInput
      id={config.name}
      maxFileSize={config.maxSizeBytes}
      helpText={config.label || "Drop a file here or click to upload"}
      subHelpText={config.helpText || `File must be less than ${maxSizeMB}MB.`}
      document={document}
      updateDocument={updateDocument}
      onDrop={onDrop}
      currentFile={currentFile}
      updateCurrentFile={updateCurrentFile}
      acceptedFileTypes={{
        "text/csv": [config.accept || ".*"],
      }}
      dropzoneIcon={
        <Icon.Document
          boxSize="element.4xl"
          color="gray.300"
          marginBottom="sm"
        />
      }
      removeButtonIcon={
        <Icon.Document color="gray.300" boxSize="element.4xl" />
      }
      handleErrorCodes={{
        [ErrorCode.FileTooLarge]: defaultErrorMessage,
        [ErrorCode.FileInvalidType]: `Only ${fileExtension} files are supported`,
        [ErrorCode.TooManyFiles]: "You can only upload one file",
      }}
    />
  );
}

function renderInput(
  config: SingleFieldConfig,
  field: ControllerRenderProps,
  form: UseFormReturn
): JSX.Element | null {
  const inputSafeProps = getInputSafeProps(config);
  switch (config.type) {
    case FORM_FIELD_TYPES.TEXT:
      return (
        <Input
          {...field}
          id={config.name}
          data-testid={config.testId || config.name}
          type={config.inputType || "text"}
          isDisabled={config.disabled}
          {...inputSafeProps}
        />
      );

    case FORM_FIELD_TYPES.EMAIL:
      return (
        <Input
          {...field}
          id={config.name}
          data-testid={config.testId || config.name}
          type="email"
          autoComplete={config.autoComplete || "email"}
          isDisabled={config.disabled}
          {...inputSafeProps}
        />
      );

    case FORM_FIELD_TYPES.PASSWORD:
      return (
        <PasswordInput
          field={field}
          config={{
            ...config,
            autoComplete: config.autoComplete || "current-password",
          }}
        />
      );

    case FORM_FIELD_TYPES.PHONE:
      return (
        <PhoneNumberInput
          {...field}
          id={config.name}
          data-testid={config.testId || config.name}
          placeholder={config.placeholder || "Phone Number"}
          phoneInput={field.value as string}
          countryInput={config.defaultCountry as PhoneCountryType}
          disabled={config.disabled}
          handleSetPhone={(value, country) => {
            field.onChange(value);
            config.onCountryChange?.(country);
            form.trigger(config.name);
          }}
        />
      );

    case FORM_FIELD_TYPES.FILE:
      return <FileUploadInput field={field} config={config} form={form} />;

    case FORM_FIELD_TYPES.IMAGE:
      return (
        <ImageUploadField
          field={field}
          config={config as ImageFormFieldConfig}
        />
      );

    case FORM_FIELD_TYPES.NUMBER: {
      const numberConfig = config as NumberFormFieldConfig;
      const hasLeftAddon = Boolean(numberConfig.leftAddon);
      const hasRightAddon = Boolean(numberConfig.rightAddon);
      const needsInputGroup = hasLeftAddon || hasRightAddon;

      const renderAddon = (addon: NumberInputAddon) => {
        if (addon.type === "icon" && addon.iconName) {
          const IconComponent = Icon[addon.iconName as keyof typeof Icon];
          if (IconComponent) {
            return (
              <IconComponent
                width="element.md"
                height="element.md"
                color="gray.500"
              />
            );
          }
        }
        return (
          <Box color="gray.500" fontSize="sm">
            {addon.content}
          </Box>
        );
      };

      const numericInput = (
        <NumericFormat
          name={field.name}
          getInputRef={field.ref}
          value={field.value}
          onChange={field.onChange}
          onBlur={() => {
            field.onBlur();
            form.trigger(config.name);
          }}
          placeholder={config.placeholder}
          decimalScale={numberConfig.decimalScale ?? 2}
          customInput={Input}
          allowNegative={numberConfig.allowNegative ?? false}
          paddingLeft={hasLeftAddon ? "32px" : undefined}
          paddingRight={hasRightAddon ? "32px" : undefined}
          data-testid={config.testId || config.name}
          id={config.name}
          isDisabled={config.disabled}
        />
      );

      if (needsInputGroup) {
        return (
          <InputGroup>
            {hasLeftAddon && numberConfig.leftAddon && (
              <InputLeftElement pointerEvents="none">
                {renderAddon(numberConfig.leftAddon)}
              </InputLeftElement>
            )}
            {numericInput}
            {hasRightAddon && numberConfig.rightAddon && (
              <InputRightElement pointerEvents="none">
                {renderAddon(numberConfig.rightAddon)}
              </InputRightElement>
            )}
          </InputGroup>
        );
      }

      return numericInput;
    }

    case FORM_FIELD_TYPES.COLOR:
      return (
        <Input
          {...field}
          id={config.name}
          data-testid={config.testId || config.name}
          type="color"
          height="element.3xl"
          width="element.6xl"
          padding="2xs"
          cursor="pointer"
          isDisabled={config.disabled}
        />
      );

    case FORM_FIELD_TYPES.TOGGLE:
      return (
        <Switch
          id={config.name}
          data-testid={config.testId || config.name}
          isChecked={Boolean(field.value)}
          onChange={(e) => field.onChange(e.target.checked)}
          isDisabled={config.disabled}
        />
      );

    case FORM_FIELD_TYPES.RADIO: {
      const radioConfig = config as RadioFormFieldConfig;
      const StackComponent = radioConfig.direction === "row" ? Stack : VStack;
      return (
        <RadioGroup
          value={field.value}
          onChange={(value) => field.onChange(value)}
          disabled={config.disabled}
        >
          <StackComponent
            align={radioConfig.direction === "row" ? "center" : "flex-start"}
            gap="sm"
            direction={radioConfig.direction === "row" ? "row" : "column"}
          >
            {radioConfig.options.map((option) => (
              <Radio key={option.id} value={option.value as string | number}>
                {option.label}
              </Radio>
            ))}
          </StackComponent>
        </RadioGroup>
      );
    }

    case FORM_FIELD_TYPES.CHECKBOX: {
      const checkboxConfig = config as CheckboxFormFieldConfig;
      return (
        <Checkbox
          id={config.name}
          name={config.name}
          data-testid={config.testId || config.name}
          isChecked={Boolean(field.value)}
          onChange={(e) => field.onChange(e.target.checked)}
          isDisabled={config.disabled}
        >
          <Text {...checkboxConfig.checkboxLabelProps}>
            {checkboxConfig.checkboxLabel}
          </Text>
        </Checkbox>
      );
    }

    case FORM_FIELD_TYPES.REPEATER:
      return <RepeaterField config={config as RepeaterFormFieldConfig} />;

    case FORM_FIELD_TYPES.TEXTAREA: {
      const textareaConfig = config as TextareaFormFieldConfig;
      return (
        <Textarea
          {...field}
          id={config.name}
          data-testid={config.testId || config.name}
          placeholder={config.placeholder}
          rows={textareaConfig.rows}
          maxLength={textareaConfig.maxLength}
          resize={textareaConfig.resize}
        />
      );
    }

    case FORM_FIELD_TYPES.RICHTEXT: {
      const richtextConfig = config as RichtextFormFieldConfig;
      const fieldError = get(form.formState.errors, config.name);
      return (
        <RichTextEditor
          id={config.name}
          value={field.value ?? ""}
          updateValue={(value) => field.onChange(value)}
          placeholder={config.placeholder}
          isInvalid={Boolean(fieldError)}
          toolbarOptions={richtextConfig.toolbarOptions}
          enableCustomImageHandler={richtextConfig.enableCustomImageHandler}
        />
      );
    }

    case FORM_FIELD_TYPES.FONT_FAMILY:
      return (
        <FontFamilyField
          value={(field.value as string) ?? ""}
          onChange={field.onChange}
        />
      );

    case FORM_FIELD_TYPES.SELECT: {
      const selectConfig = config as SelectFormFieldConfig;
      const selectedItem = selectConfig.options.find(
        (opt) => opt.id === field.value
      );

      return (
        <Select
          id={config.name}
          data-testid={config.testId || config.name}
          items={selectConfig.options}
          selectedItem={selectedItem || selectConfig.options[0]}
          onSelectedItemChange={({ selectedItem: newItem }) => {
            if (newItem) {
              const value = selectConfig.valueKey
                ? newItem[selectConfig.valueKey as keyof typeof newItem]
                : newItem.id;
              field.onChange(value);
            }
          }}
          isLoading={selectConfig.isLoading}
        />
      );
    }

    case FORM_FIELD_TYPES.DATE: {
      const dateConfig = config as DateFormFieldConfig;
      const selectedDate = field.value
        ? new Date(field.value as string)
        : undefined;

      return (
        <InputDatePicker
          mode="single"
          selected={selectedDate}
          onChangeSelected={({ selected }) => {
            if (selected) {
              field.onChange((selected as Date).toISOString());
            }
          }}
          disablePastDates={
            dateConfig.minDate
              ? dateConfig.minDate.getTime() >= new Date().setHours(0, 0, 0, 0)
              : false
          }
          numberOfMonths={1}
        />
      );
    }

    // TODO: replace this with DateTimePicker.web.tsx. Currently we don't have a way to show the datetime as blank so using this for now
    case FORM_FIELD_TYPES.DATETIME: {
      const datetimeConfig = config as DatetimeFormFieldConfig;
      return (
        <Input
          type="datetime-local"
          value={(field.value as string) || ""}
          onChange={(e) => field.onChange(e.target.value)}
          min={datetimeConfig.min}
          max={datetimeConfig.max}
          data-testid={datetimeConfig.testId || `${datetimeConfig.name}-input`}
        />
      );
    }

    case FORM_FIELD_TYPES.SEARCH:
      return (
        <SearchField
          field={field}
          config={
            config as SearchFormFieldConfig<{
              id: string | number;
              name?: string;
            }>
          }
        />
      );

    case FORM_FIELD_TYPES.SEARCH_EVENT:
      return (
        <SearchEventField
          field={field}
          config={config as SearchEventFormFieldConfig}
          form={form}
        />
      );

    case FORM_FIELD_TYPES.SEARCH_PRODUCT:
      return (
        <SearchProductField
          field={field}
          config={config as SearchProductFormFieldConfig}
          form={form}
        />
      );

    case FORM_FIELD_TYPES.SEARCH_MEMBERSHIP:
      return (
        <SearchMembershipField
          field={field}
          config={config as SearchMembershipFormFieldConfig}
          form={form}
        />
      );

    default:
      return null;
  }
}

/**
 * Image Upload Field Component
 */
function ImageUploadField({
  field,
  config,
}: {
  field: ControllerRenderProps;
  config: ImageFormFieldConfig;
}) {
  // Apply defaults
  const maxFileSize = config.maxFileSize ?? 3000000;
  const minWidth = config.minWidth ?? 100;
  const minHeight = config.minHeight ?? 100;
  const maxWidth = config.maxWidth ?? 2000;
  const maxHeight = config.maxHeight ?? 2000;
  const maxFileSizeMB = Math.round(maxFileSize / 1000000);

  const defaultErrorMessages = {
    fileTooLarge: `File cannot be larger than ${maxFileSizeMB}MB`,
    fileInvalidType: "Only jpg and png images are supported",
    fileMinDimensions: `Image must be at least ${minWidth}x${minHeight} pixels`,
    fileMaxDimensions: `Image cannot exceed ${maxWidth}x${maxHeight} pixels`,
    tooManyFiles: "You can only upload one image",
  };

  const errorMessages = {
    ...defaultErrorMessages,
    ...config.errorMessages,
  };

  // Generate default subHelpText if not provided
  const defaultSubHelpText = `JPG or PNG format, ${minWidth}x${minHeight} to ${maxWidth}x${maxHeight} pixels, max ${maxFileSizeMB}MB`;

  return (
    <ImageUploadInput
      id={config.name}
      image={field.value ?? ""}
      updateImage={(value: string | undefined) => {
        // Use empty string instead of undefined for form compatibility
        field.onChange(value ?? "");
      }}
      handleErrorCodes={{
        "file-too-large": errorMessages.fileTooLarge,
        "file-invalid-type": errorMessages.fileInvalidType,
        "file-min-dimensions": errorMessages.fileMinDimensions,
        "file-max-dimensions": errorMessages.fileMaxDimensions,
        "too-many-files": errorMessages.tooManyFiles,
      }}
      helpText={config.placeholder || "Drop an image here or click to upload"}
      subHelpText={config.subHelpText ?? defaultSubHelpText}
      maxFileSize={maxFileSize}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      cropAspectRatio={config.cropAspectRatio}
      mimeType={config.mimeType}
      variant={config.variant}
      avatarSize={config.avatarSize}
      editButtonAriaLabel={config.editButtonAriaLabel}
    />
  );
}

/**
 * Helper to get value from item using config
 */
function getItemValue<T>(
  item: ComboBoxInputItemType,
  options: T[],
  config: SearchFormFieldConfig<T>
): unknown {
  const originalItem = options.find(
    (opt) => (opt as { id?: string | number }).id === item.id
  );
  if (!originalItem) {
    return item.id;
  }
  if (config.getOptionValue) {
    return config.getOptionValue(originalItem);
  }
  return originalItem;
}

/**
 * Counter component for displaying counts (e.g., "5 Events, 12 Ticket types")
 */
function SearchFieldCounter({
  items,
}: {
  items: Array<{ label: string; count: number }>;
}): JSX.Element {
  return (
    <Flex justify="space-between" mt="xs">
      {items.map((item) => (
        <Text key={item.label} fontSize="sm">
          <Text as="span" fontWeight="bold">
            {item.count}
          </Text>{" "}
          {item.label}
        </Text>
      ))}
    </Flex>
  );
}

/**
 * Generic Search Field Component using SearchItem
 * Supports both single and multi-select with async data fetching
 * Enhanced with renderSelectedItem, counters, and hideSelectedTags support
 */
function SearchField<T extends { id: number | string; name?: string }>({
  field,
  config,
}: {
  field: ControllerRenderProps;
  config: SearchFormFieldConfig<T>;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<ComboBoxInputItemType[]>(
    []
  );
  // Store full item data for renderSelectedItem
  const [selectedItemsData, setSelectedItemsData] = useState<T[]>([]);

  const minSearchLength = config.minSearchLength ?? 2;
  const debounceMs = config.debounceMs ?? 300;
  const [debouncedQuery] = useDebounce(searchQuery, debounceMs);

  const isSingleSelect = config.single !== false;
  const hasCustomSelectedRenderer = !!config.renderSelectedItem;

  // Fetch options when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length >= minSearchLength) {
      setIsLoading(true);
      config
        .fetchOptions(debouncedQuery)
        .then((results) => {
          setOptions(results);
        })
        .catch(() => {
          setOptions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setOptions([]);
    }
  }, [debouncedQuery, minSearchLength, config]);

  // Sync selected items when field value changes externally (e.g., form reset)
  useEffect(() => {
    if (!field.value) {
      setSelectedItems([]);
      setSelectedItemsData([]);
    }
  }, [field.value]);

  // Map options to ComboBoxInputItemType format
  const items: ComboBoxInputItemType[] = useMemo(
    () =>
      options.map((item) => ({
        ...item,
        id: item.id as string | number,
        name: config.getOptionLabel(item),
      })),
    [options, config]
  );

  // For single select: handles selection from SearchItem
  const handleSingleSelectChange = useCallback(
    (item: ComboBoxInputItemType) => {
      if (item) {
        const fullItem = options.find((opt) => opt.id === item.id) as T;
        const value = config.getOptionValue
          ? config.getOptionValue(fullItem || (item as T))
          : fullItem || item;
        field.onChange(value);
        setSelectedItems([
          {
            id: item.id as string | number,
            name: config.getOptionLabel(fullItem || (item as T)),
          },
        ]);
        setSelectedItemsData(fullItem ? [fullItem] : []);
      }
    },
    [config, field, options]
  );

  // For multi-select: handles selection from ComboBoxMultiSelect
  const handleDropdownClickChange = useCallback(
    (item: ComboBoxInputItemType) => {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);

      // Find the full item data from options
      const fullItem = options.find((opt) => opt.id === item.id);
      if (fullItem) {
        setSelectedItemsData((prev) => [...prev, fullItem]);
      }

      const values = newSelectedItems.map((selected) =>
        getItemValue(selected, options, config)
      );
      field.onChange(values);
    },
    [config, field, options, selectedItems]
  );

  // Handles removing selected items (both single and multi)
  const handleRemoveItem = useCallback(
    (itemId: string | number) => {
      const newSelectedItems = selectedItems.filter(
        (selected) => selected.id !== itemId
      );
      setSelectedItems(newSelectedItems);
      setSelectedItemsData((prev) => prev.filter((item) => item.id !== itemId));

      if (isSingleSelect) {
        field.onChange(null);
      } else {
        const values = newSelectedItems.map((selected) =>
          getItemValue(selected, options, config)
        );
        field.onChange(values.length > 0 ? values : null);
      }
    },
    [config, field, isSingleSelect, options, selectedItems]
  );

  // Handler for SearchItem - single select uses this for selection, multi uses for removal
  const handleSelectedItemChange = useCallback(
    (item: ComboBoxInputItemType) => {
      if (isSingleSelect) {
        handleSingleSelectChange(item);
      } else {
        handleRemoveItem(item.id);
      }
    },
    [isSingleSelect, handleSingleSelectChange, handleRemoveItem]
  );

  const handleInputValueChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const labelText = config.placeholder || config.searchPlaceholder || "";
  const placeholderText = config.searchPlaceholder || config.placeholder || "";

  const renderListItem = config.listItemTemplate
    ? (item: T): React.ReactElement => {
        const originalItem = options.find((opt) => opt.id === item.id);
        if (config.listItemTemplate) {
          return config.listItemTemplate(
            originalItem || item
          ) as React.ReactElement;
        }
        return <Text>{item.name}</Text>;
      }
    : undefined;

  const noopFetchNextPage = useCallback(() => {
    // No pagination support yet
  }, []);

  // Calculate counter values
  const counterItems = useMemo(() => {
    if (!config.counters || !field.value) return [];
    const values = Array.isArray(field.value) ? field.value : [field.value];
    return config.counters.map((counter) => {
      const count = counter.getValue(values);
      const label =
        typeof counter.label === "function"
          ? counter.label(count)
          : counter.label;
      return { label, count };
    });
  }, [config.counters, field.value]);

  // Determine if we should hide the default selected tags
  const shouldHideSelectedTags =
    config.hideSelectedTags || hasCustomSelectedRenderer;

  return (
    <Flex direction="column" gap="md" width="100%">
      {/* Counter display */}
      {config.counters &&
        counterItems.length > 0 &&
        selectedItems.length > 0 && <SearchFieldCounter items={counterItems} />}

      {/* Search input */}
      <SearchItem<T>
        id={config.name}
        single={isSingleSelect}
        multiple={!isSingleSelect}
        label={labelText}
        placeholder={placeholderText}
        items={items as T[]}
        selectedSet={shouldHideSelectedTags ? [] : (selectedItems as T[])}
        isFetching={isLoading}
        hasNextPage={false}
        fetchNextPage={noopFetchNextPage}
        isFetchingNextPage={false}
        handleInputValueChange={handleInputValueChange}
        onDropdownClickChange={
          isSingleSelect ? undefined : handleDropdownClickChange
        }
        handleSelectedItemChange={handleSelectedItemChange}
        listItemTemplate={renderListItem}
      />

      {/* Custom selected items list */}
      {hasCustomSelectedRenderer && !isSingleSelect && (
        <Flex direction="column" gap="md">
          {selectedItemsData.map((item, index) => {
            const value = Array.isArray(field.value)
              ? field.value[index]
              : field.value;
            return (
              <React.Fragment key={item.id}>
                {config.renderSelectedItem?.({
                  item,
                  value,
                  index,
                  onRemove: () => handleRemoveItem(item.id as string | number),
                })}
              </React.Fragment>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
}

// SearchEventField, SearchProductField, and SearchMembershipField are
// imported from ./SearchFormFields.web.tsx
